/**
 * Tech-debt #4 — Certificate family extracted from csmsCallHandler. v2.0.1
 * security profile: install / delete / list certificates against the in-memory
 * store, and accept a CSMS-signed chain. No real cryptography — a deterministic
 * CertificateHashData is synthesised from the PEM so Get/Delete round-trip.
 */
import { csmsSession, type CertificateRecord } from '../csmsSessionState';
import type { CsmsCtx, CsmsEvent } from './context';

/** Deterministic hex string of `chars` length, derived from `seed` (FNV-ish; sim-only, not crypto). */
function detHex(seed: string, chars: number): string {
  let h = 2166136261 >>> 0;
  let out = '';
  let salt = 0;
  while (out.length < chars) {
    for (let k = 0; k < seed.length; k++) {
      h ^= seed.charCodeAt(k);
      h = Math.imul(h, 16777619) >>> 0;
    }
    h = (h + salt++) >>> 0;
    out += h.toString(16).padStart(8, '0');
  }
  return out.slice(0, chars);
}

function synthHashData(certificate: string): CertificateRecord['hashData'] {
  return {
    hashAlgorithm: 'SHA256',
    issuerNameHash: detHex(certificate, 64),
    issuerKeyHash: detHex(`${certificate}|key`, 64),
    serialNumber: detHex(`${certificate}|sn`, 16),
  };
}

function sameHashData(a: Record<string, unknown>, b: CertificateRecord['hashData']): boolean {
  return (
    a['hashAlgorithm'] === b.hashAlgorithm &&
    a['issuerNameHash'] === b.issuerNameHash &&
    a['issuerKeyHash'] === b.issuerKeyHash &&
    a['serialNumber'] === b.serialNumber
  );
}

export function handleCertificateSigned(_event: CsmsEvent<'CertificateSigned'>, ctx: CsmsCtx): void {
  // CSMS returns a signed certificate chain. No real verification; accept.
  // CertificateSignedStatus { Accepted, Rejected }.
  ctx.dispatcher.reply(ctx.messageId, { status: 'Accepted' });
}

export function handleInstallCertificate(
  event: CsmsEvent<'InstallCertificate'>,
  ctx: CsmsCtx,
): void {
  // InstallCertificateStatus { Accepted, Rejected, Failed }. Synthesise a
  // hash + store so Get/Delete round-trip.
  csmsSession.addCertificate(ctx.stationId, {
    certificateType: event.certificateType,
    hashData: synthHashData(event.certificate),
  });
  ctx.dispatcher.reply(ctx.messageId, { status: 'Accepted' });
}

export function handleDeleteCertificate(
  event: CsmsEvent<'DeleteCertificate'>,
  ctx: CsmsCtx,
): void {
  // DeleteCertificateStatus { Accepted, Failed, NotFound }.
  const removed = csmsSession.removeCertificate(ctx.stationId, (c) =>
    sameHashData(event.hashData, c.hashData),
  );
  ctx.dispatcher.reply(ctx.messageId, { status: removed ? 'Accepted' : 'NotFound' });
}

export function handleGetInstalledCertificateIds(
  event: CsmsEvent<'GetInstalledCertificateIds'>,
  ctx: CsmsCtx,
): void {
  // GetInstalledCertificateStatus { Accepted, NotFound }.
  const list = csmsSession.listCertificates(ctx.stationId);
  const matched =
    event.certificateTypes.length === 0
      ? list
      : list.filter((c) => event.certificateTypes.includes(c.certificateType));
  if (matched.length === 0) {
    ctx.dispatcher.reply(ctx.messageId, { status: 'NotFound' });
    return;
  }
  ctx.dispatcher.reply(ctx.messageId, {
    status: 'Accepted',
    certificateHashDataChain: matched.map((c) => ({
      certificateType: c.certificateType,
      certificateHashData: c.hashData,
    })),
  });
}
